import * as React from 'react';
import { Loader2 } from 'lucide-react';
import PlatformSettingsLayout from '@/components/settings/platform/PlatformSettingsLayoutV2';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { Textarea } from '@ampm-7en-ai/ui/components/Textarea';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { Callout } from '@ampm-7en-ai/ui/components/Callout';
import { Skeleton } from '@ampm-7en-ai/ui/components/Skeleton';
import { usePlatformSettings, useUpdatePlatformSettings } from '@/hooks/usePlatformSettings';

/**
 * What the platform calls itself, and the one switch that takes it offline.
 *
 * Maintenance mode is the only thing here that changes what customers see, so
 * it is kept apart from the names and addresses above it and says what it does
 * before it is flipped.
 */

interface Draft {
  platform_name: string;
  platform_description: string;
  support_email: string;
  maintenance_mode: boolean;
}

const EMPTY: Draft = {
  platform_name: '',
  platform_description: '',
  support_email: '',
  maintenance_mode: false,
};

/** An address the server will accept, or a reason it won't. */
const emailProblem = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return 'Give an address people can write to for help.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'That doesn’t look like an email address.';
  return null;
};

export default function GeneralSettingsV2() {
  const { data: settings, isLoading, error } = usePlatformSettings();
  const update = useUpdatePlatformSettings();

  const [draft, setDraft] = React.useState<Draft>(EMPTY);
  const [saved, setSaved] = React.useState<Draft>(EMPTY);

  // What arrived is both the form's starting point and what "changed" means.
  React.useEffect(() => {
    if (!settings) return;
    const incoming: Draft = {
      platform_name: settings.platform_name || '',
      platform_description: settings.platform_description || '',
      support_email: settings.support_email || '',
      maintenance_mode: settings.maintenance_mode || false,
    };
    setDraft(incoming);
    setSaved(incoming);
  }, [settings]);

  const set = (change: Partial<Draft>) => setDraft((current) => ({ ...current, ...change }));

  const problem = emailProblem(draft.support_email);
  const changed = (Object.keys(draft) as (keyof Draft)[]).some((key) => draft[key] !== saved[key]);
  const blocked = Boolean(problem) || !draft.platform_name.trim();

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (blocked || !changed) return;
    try {
      await update.mutateAsync(draft);
      setSaved(draft);
    } catch {
      // The mutation raises its own toast; the form keeps what was typed.
    }
  };

  if (error) {
    return (
      <PlatformSettingsLayout title="General" description="What the platform calls itself, and who to write to.">
        <Callout tone="danger" title="Couldn’t load the settings">
          {error instanceof Error ? error.message : 'Something went wrong fetching the platform settings.'}
        </Callout>
      </PlatformSettingsLayout>
    );
  }

  return (
    <PlatformSettingsLayout title="General" description="What the platform calls itself, and who to write to.">
      <form onSubmit={save}>
        <Panel padding="md">
          <h2 className="text-ui-base font-medium text-ui-fg">Platform</h2>
          <p className="mt-0.5 text-ui-xs text-ui-fg-muted">
            The name and description appear wherever the platform introduces itself.
          </p>

          {isLoading ? (
            <div className="mt-5 space-y-5">
              <Skeleton className="h-[38px] w-full rounded-ui-md" />
              <Skeleton className="h-24 w-full rounded-ui-md" />
              <Skeleton className="h-[38px] w-full rounded-ui-md" />
            </div>
          ) : (
            <div className="mt-5 space-y-5">
              <Field label="Name" htmlFor="platform-name">
                <Input
                  id="platform-name"
                  value={draft.platform_name}
                  onChange={(event) => set({ platform_name: event.target.value })}
                  placeholder="7en"
                />
              </Field>

              <Field
                label="Description"
                htmlFor="platform-description"
                hint="A line or two. Shown where the platform needs to explain itself."
              >
                <Textarea
                  id="platform-description"
                  rows={3}
                  value={draft.platform_description}
                  onChange={(event) => set({ platform_description: event.target.value })}
                  placeholder="The multi-agent platform for customer conversations."
                />
              </Field>

              <Field
                label="Support email"
                htmlFor="support-email"
                error={draft.support_email ? problem ?? undefined : undefined}
                hint="Where customers are told to write when they need a person."
              >
                <Input
                  id="support-email"
                  type="email"
                  value={draft.support_email}
                  onChange={(event) => set({ support_email: event.target.value })}
                  placeholder="support@7en.ai"
                />
              </Field>
            </div>
          )}
        </Panel>

        {/* Apart from the rest: this one is visible to every customer. */}
        <Panel padding="md" className="mt-4">
          <ToggleRow
            label="Maintenance mode"
            description="Takes the platform offline for everyone but you, and shows them a holding page. Nothing is deleted."
            checked={draft.maintenance_mode}
            onCheckedChange={(maintenance_mode) => set({ maintenance_mode })}
            disabled={isLoading}
          />
          {draft.maintenance_mode && !saved.maintenance_mode && (
            <Callout tone="warning" className="mt-4" title="This goes live when you save">
              Everyone signed in to a workspace will be shown the holding page instead of their agents.
            </Callout>
          )}
        </Panel>

        <div className="mt-4 flex items-center justify-end gap-3">
          {changed && !update.isPending && <p className="text-ui-xs text-ui-fg-subtle">Unsaved changes</p>}
          <Button type="submit" variant="primary" disabled={update.isPending || isLoading || blocked || !changed}>
            {update.isPending ? (
              <>
                <Loader2 className="animate-spin" /> Saving…
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </div>
      </form>
    </PlatformSettingsLayout>
  );
}
